import React, { useRef } from 'react';

import {
  RCModalBase,
  RCModalBaseRootProps,
  useRCModalBaseContext,
} from '../internalComponents/RCModalBase/RCModalBase';
import { RCCloseButton } from '../RCCloseButton/RCCloseButton';
import { RCThemeOverride } from '../RCThemeContext/RCThemeOverride';

interface RCLightboxProps {
  /** Controls whether the lightbox is open, paired with `onOpenChange`. */
  isOpen: RCModalBaseRootProps['isOpen'];
  /** Called with `false` when the close button or Escape closes the lightbox. */
  onOpenChange: RCModalBaseRootProps['onOpenChange'];
  /** Names the dialog for screen readers, such as the listing or product being viewed. It isn't shown. */
  label: string;
  /** The media to view. */
  children: React.ReactNode;
}

/**
 * A full-screen dark frame for viewing media: a dialog with a close button, Escape to close, and
 * no layout of its own. It is not a gallery: for a set of images with thumbnails and navigation,
 * use `LightboxImage` from commons.
 *
 * Children fill the frame. It scrolls vertically when they're taller than the viewport, and
 * absolutely positioned children are placed against it.
 *
 * The frame defines an inset area as `--lightbox-inset-top`, `-right` and `-left`:
 * spacing from each edge plus the browser safe area. The close button sits on it; place content
 * and controls against the edges with the same properties so they line up.
 */
export function RCLightbox({
  isOpen,
  onOpenChange,
  label,
  children,
}: RCLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <RCModalBase.Root
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      initialFocus={closeButtonRef}
      role="dialog"
      // Nothing outside the frame is visible to click.
      useDismissProps={{ outsidePress: false }}
    >
      <RCModalBase.Content
        className="rc-lightbox"
        displayStyle="fullscreen"
      >
        <RCThemeOverride theme="dark">
          <div className="rc-lightbox__body">
            <RCModalBase.Heading className="visually-hidden">
              {label}
            </RCModalBase.Heading>
            {children}
            <CloseButton ref={closeButtonRef} />
          </div>
        </RCThemeOverride>
      </RCModalBase.Content>
    </RCModalBase.Root>
  );
}

const CloseButton = React.forwardRef<HTMLButtonElement>(function CloseButton(_props, ref) {
  const { close } = useRCModalBaseContext();

  return (
    <div className="rc-lightbox__close">
      <RCCloseButton
        ref={ref}
        variant="elevated-light"
        size="medium"
        onClick={close}
      />
    </div>
  );
});
